import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { positionHoldings, toNumber } from '../utils/calc';
import { applyImportPlan } from '../utils/holdingsImport';

const HOLDINGS_COLLECTION = 'holdings';
const FUND_COLLECTION = 'fund';
const FUND_META_DOC = 'meta';

/** Fields persisted for each holding (raw facts only - derived values are computed). */
const HOLDING_FIELDS = [
  'name',
  'ticker',
  'shares',
  'costBasis',
  'currentPrice',
  'marketValue',
  'dividendYield',
  'cashDividends',
  'reinvestedDividends',
  'order',
];

/** Keep only known holding fields and coerce numerics before writing. */
const sanitizeHolding = (holding, index) => {
  const out = {};
  HOLDING_FIELDS.forEach((field) => {
    if (field === 'name' || field === 'ticker') {
      out[field] = holding[field] ?? '';
    } else if (field === 'order') {
      out.order = typeof index === 'number' ? index : toNumber(holding.order) ?? 0;
    } else {
      out[field] = toNumber(holding[field]);
    }
  });
  return out;
};

export const holdingsService = {
  /**
   * Subscribe to real-time updates for all holdings.
   * @returns {Function} Unsubscribe function
   */
  subscribeToHoldings: (callback, errorCallback) => {
    const q = query(collection(db, HOLDINGS_COLLECTION), orderBy('order', 'asc'));
    return onSnapshot(
      q,
      (snapshot) => {
        const holdings = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        callback(holdings);
      },
      errorCallback
    );
  },

  /** Get all holdings (one-time read). */
  getAllHoldings: async () => {
    const q = query(collection(db, HOLDINGS_COLLECTION), orderBy('order', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  /** Add a new holding. */
  addHolding: async (holdingData) => {
    const docRef = await addDoc(collection(db, HOLDINGS_COLLECTION), {
      ...sanitizeHolding(holdingData, holdingData.order),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  },

  /** Update an existing holding. */
  updateHolding: async (id, holdingData) => {
    const docRef = doc(db, HOLDINGS_COLLECTION, id);
    await updateDoc(docRef, {
      ...sanitizeHolding(holdingData, holdingData.order),
      updatedAt: serverTimestamp(),
    });
  },

  /** Delete a holding. */
  deleteHolding: async (id) => {
    await deleteDoc(doc(db, HOLDINGS_COLLECTION, id));
  },

  /**
   * Atomically save all holdings in a single batch.
   * Updates existing docs, creates new ones, and stamps a stable `order`.
   */
  saveAllHoldings: async (holdings) => {
    const batch = writeBatch(db);
    positionHoldings(holdings).forEach((holding, index) => {
      const data = {
        ...sanitizeHolding(holding, index),
        updatedAt: serverTimestamp(),
      };
      if (holding.id) {
        batch.set(doc(db, HOLDINGS_COLLECTION, holding.id), data, { merge: true });
      } else {
        const ref = doc(collection(db, HOLDINGS_COLLECTION));
        batch.set(ref, { ...data, createdAt: serverTimestamp() });
      }
    });
    await batch.commit();
  },

  /** Read fund-level metadata (currently the cash balance). */
  getFundMeta: async () => {
    const snap = await getDoc(doc(db, FUND_COLLECTION, FUND_META_DOC));
    return snap.exists() ? snap.data() : { cash: 0 };
  },

  /** Subscribe to fund-level metadata. */
  subscribeToFundMeta: (callback, errorCallback) =>
    onSnapshot(
      doc(db, FUND_COLLECTION, FUND_META_DOC),
      (snap) => callback(snap.exists() ? snap.data() : { cash: 0 }),
      errorCallback
    ),

  /** Set the fund cash balance. */
  setCash: async (cash) => {
    await setDoc(
      doc(db, FUND_COLLECTION, FUND_META_DOC),
      { cash: toNumber(cash) || 0, updatedAt: serverTimestamp() },
      { merge: true }
    );
  },

  /**
   * One-time normalization of legacy string-based holdings.
   * - Converts string fields to numbers and drops derived fields.
   * - Moves the "Cash" summary row into fund/meta.
   * - Removes the "Equities Total" / "Portfolio Value" summary rows
   *   (these are now computed at render time).
   * Returns a summary of what changed.
   */
  normalizeLegacyData: async () => {
    const existing = await holdingsService.getAllHoldings();
    const batch = writeBatch(db);
    let cash = null;
    let removed = 0;
    let converted = 0;
    let order = 0;

    existing.forEach((h) => {
      const name = h.name || h.equities || '';
      const lower = name.trim().toLowerCase();

      if (lower === 'cash') {
        cash = toNumber(h.marketValue) || 0;
        batch.delete(doc(db, HOLDINGS_COLLECTION, h.id));
        removed += 1;
        return;
      }
      if (lower === 'equities total' || lower === 'portfolio value') {
        batch.delete(doc(db, HOLDINGS_COLLECTION, h.id));
        removed += 1;
        return;
      }

      const normalized = {
        name,
        ticker: h.ticker && h.ticker !== '--' ? h.ticker : '',
        shares: toNumber(h.shares),
        costBasis: toNumber(h.costBasis),
        currentPrice: toNumber(h.currentPrice),
        marketValue: toNumber(h.marketValue),
        dividendYield: toNumber(h.dividendYield),
        cashDividends: toNumber(h.cashDividends),
        reinvestedDividends: toNumber(h.reinvestedDividends),
        order: order++,
        updatedAt: serverTimestamp(),
      };
      batch.set(doc(db, HOLDINGS_COLLECTION, h.id), normalized);
      converted += 1;
    });

    if (cash !== null) {
      batch.set(
        doc(db, FUND_COLLECTION, FUND_META_DOC),
        { cash, updatedAt: serverTimestamp() },
        { merge: true }
      );
    }

    await batch.commit();
    return { converted, removed, cash };
  },

  /**
   * Merge CSV-imported rows into existing holdings (match on ticker, then name).
   * Updates matched positions, appends new ones; never deletes omitted rows.
   */
  importMerge: async (plan) => {
    const existing = await holdingsService.getAllHoldings();
    const merged = applyImportPlan(plan, existing);
    await holdingsService.saveAllHoldings(merged);
    return {
      updated: plan.updates.length,
      added: plan.adds.length,
      skipped: plan.skipped.length,
    };
  },
};
